import { type RendererCapabilities } from '@a2ui/web_core/v0_9'

import { createSseEventBus } from '@webapp/core/services/EventBus'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { EventBusConnectionStates } from '@webapp/infrastructure/interfaces/services/IEventBus'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type IConversationRepository,
  type ConversationStreamHandlers,
} from '../../business-logic/interfaces/IConversationRepository'
import { type ConversationTechnicalMessagePayload } from '../../business-logic/types/ConversationTechnicalMessagePayload'
import { type ConversationTurnPayload } from '../../business-logic/types/ConversationTurnPayload'
import { type ConversationTurnItemConstructor } from '../../value-objects/ConversationTurnItem'
import {
  ConversationStreamEventTypes,
  type ConversationEventMap,
} from '../api-models/ConversationEventApiModel'
import { type ConversationTranscriptResponsePayload } from '../api-models/ConversationTranscriptApiModel'
import {
  mapConversationEnvelopeEvent,
  mapConversationTelemetryEvent,
} from '../mappers/mapConversationEventApiModel'
import { mapConversationTechnicalMessage } from '../mappers/mapConversationTechnicalMessage'
import { mapConversationTranscriptApiModel } from '../mappers/mapConversationTranscriptApiModel'

const AGENT_ENDPOINT = '/api/v3/global-command/agent/'
const RESUME_ENDPOINT = '/api/v3/global-command/agent/resume/'

const START_REQUEST_MESSAGE = "I'd like to create a new request"

// The end event carries no message; BE error copy is not localized either.
const TURN_FAILED_MESSAGE =
  'Procurify AI ran into a problem answering that. Please try again.'

interface Dependency {
  httpClient: IHttpClient
  loadA2uiClientCapabilities: () => Promise<RendererCapabilities>
}

export class ConversationRepository implements IConversationRepository {
  constructor(private readonly deps: Dependency) {}

  async resume(
    conversationId: string
  ): Promise<ConversationTurnItemConstructor[]> {
    try {
      const response =
        await this.deps.httpClient.get<ConversationTranscriptResponsePayload>(
          `${RESUME_ENDPOINT}${conversationId}/`
        )

      return mapConversationTranscriptApiModel(response.data.data)
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  sendTechnicalMessage(
    payload: ConversationTechnicalMessagePayload,
    handlers: ConversationStreamHandlers
  ): () => void {
    return this.openTurn(
      {
        message: mapConversationTechnicalMessage(payload.technicalMessage),
        conversationId: payload.conversationId,
      },
      handlers
    )
  }

  openTurn(
    payload: ConversationTurnPayload,
    handlers: ConversationStreamHandlers
  ): () => void {
    let isClosed = false
    let disconnect: Nullable<() => void> = null

    this.deps
      .loadA2uiClientCapabilities()
      .then((capabilities) => {
        if (isClosed) return
        disconnect = this.connectTurn(payload, capabilities, handlers)
      })
      .catch(() => {
        if (isClosed) return
        isClosed = true
        handlers.onError({ code: 'turn_failed', message: TURN_FAILED_MESSAGE })
        handlers.onDone()
      })

    return () => {
      if (disconnect) {
        disconnect()
        return
      }
      if (isClosed) return
      isClosed = true
      handlers.onDone()
    }
  }

  private connectTurn(
    payload: ConversationTurnPayload,
    a2uiClientCapabilities: RendererCapabilities,
    handlers: ConversationStreamHandlers
  ): () => void {
    const bus = createSseEventBus<ConversationEventMap>({
      url: AGENT_ENDPOINT,
      body: {
        message:
          payload.message === '' ? START_REQUEST_MESSAGE : payload.message,
        ...(payload.conversationId !== null && {
          conversation_id: payload.conversationId,
        }),
        ...(typeof payload.startContext?.primaryCategoryId === 'number' && {
          location_id: payload.startContext.primaryCategoryId,
        }),
        ...(typeof payload.startContext?.secondaryCategoryId === 'number' && {
          department_id: payload.startContext.secondaryCategoryId,
        }),
        a2ui_client_capabilities: a2uiClientCapabilities,
      },
      headers: { Accept: 'text/event-stream, */*' },
    })

    let isDone = false
    const finish = (): void => {
      if (isDone) {
        return
      }
      isDone = true
      handlers.onDone()
    }

    const finishAsInterrupted = (): void => {
      if (isDone) {
        return
      }
      handlers.onError({
        code: 'turn_interrupted',
        message: TURN_FAILED_MESSAGE,
      })
      finish()
    }

    let hasStarted = false
    const startSession = (conversationId: string): void => {
      if (hasStarted) {
        return
      }
      hasStarted = true
      handlers.onSessionStart(conversationId)
    }

    bus.bind('message', (event) => {
      if (isDone) {
        return
      }

      handlers.onTelemetry(mapConversationTelemetryEvent(event))

      switch (event.type) {
        case ConversationStreamEventTypes.CONVERSATION_START:
        case ConversationStreamEventTypes.SESSION_START:
          startSession(event.data.conversation_id)
          break
        case ConversationStreamEventTypes.THINKING:
          handlers.onProgress(event.data.label)
          break
        case ConversationStreamEventTypes.UI_UPDATE:
          handlers.onItem(mapConversationEnvelopeEvent(event.data.envelope))
          break
        case ConversationStreamEventTypes.MESSAGE_FINAL:
          handlers.onItem(mapConversationEnvelopeEvent(event.data.envelope))
          finish()
          break
        case ConversationStreamEventTypes.OUT_OF_SCOPE:
          finish()
          break
        case ConversationStreamEventTypes.ERROR:
          handlers.onError({
            code: event.data.code,
            message: event.data.message,
          })
          finish()
          break
        case ConversationStreamEventTypes.CONVERSATION_END:
          if (event.data.status !== 'ok') {
            handlers.onError({
              code: 'turn_failed',
              message: TURN_FAILED_MESSAGE,
            })
          }
          finish()
          break
        default:
          break
      }
    })

    bus.bind('state', ({ state }) => {
      if (state === EventBusConnectionStates.DISCONNECTED) {
        finishAsInterrupted()
      }
    })

    bus.bind('error', (error) => {
      handlers.onError({ code: error.code, message: error.message })
      finish()
    })

    bus.connect()

    return () => {
      bus.disconnect()
      finish()
    }
  }
}
